<!-- ─── Page Wrapper ──────────────────────────────────────────────────────────── -->
<div class="min-h-screen bg-slate-50 py-10 px-4">
    <div class="max-w-5xl mx-auto">
  
      <!-- Page Header -->
      <div class="mb-8">
        <p class="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-1">Admin Panel</p>
        <h1 class="text-3xl font-bold text-slate-800 tracking-tight">Grant Premium Access</h1>
        <p class="mt-1 text-slate-500 text-sm">Elevate users to premium status by their registered phone number.</p>
      </div>
  
      <!-- ─── Two-Column Layout ──────────────────────────────────────────────────── -->
      <div class="flex flex-col lg:flex-row gap-6 items-start">
  
        <!-- ══ LEFT: Info Card ════════════════════════════════════════════════════ -->
        <div class="w-full lg:w-80 shrink-0">
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 class="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Who can grant premium?</h2>
  
            <div class="space-y-5">
  
              <!-- Super Admin -->
              <div class="flex gap-3">
                <div class="mt-0.5 shrink-0 w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <svg class="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-slate-800">Super Admin</p>
                  <p class="text-xs text-slate-500 leading-relaxed mt-0.5">Full platform access — can grant, revoke, and manage premium for any user.</p>
                </div>
              </div>
  
              <div class="border-t border-slate-100"></div>
  
              <!-- Project Developer -->
              <div class="flex gap-3">
                <div class="mt-0.5 shrink-0 w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                  <svg class="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-slate-800">Project Developer</p>
                  <p class="text-xs text-slate-500 leading-relaxed mt-0.5">Unlocks advanced API limits, debug tools, and preview features.</p>
                </div>
              </div>
  
              <div class="border-t border-slate-100"></div>
  
              <!-- Product Owner -->
              <div class="flex gap-3">
                <div class="mt-0.5 shrink-0 w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                  <svg class="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-slate-800">Product Owner</p>
                  <p class="text-xs text-slate-500 leading-relaxed mt-0.5">Enables priority support, analytics dashboards, and early releases.</p>
                </div>
              </div>
  
            </div>
  
            <div class="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p class="text-xs text-slate-500 leading-relaxed">
                <span class="font-medium text-slate-700">Note:</span>
                Premium is tied to the user's registered phone number. Ensure it is active and verified.
              </p>
            </div>
          </div>
        </div>
        <!-- End Left -->
  
        <!-- ══ RIGHT: Form + Result ═══════════════════════════════════════════════ -->
        <div class="flex-1 space-y-5">
  
          <!-- ════ FORM CARD ════════════════════════════════════════════════════ -->
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8">
  
            <div class="mb-6">
              <h2 class="text-lg font-bold text-slate-800">Phone Numbers</h2>
              <p class="text-sm text-slate-500 mt-0.5">
                Enter one or more registered phone numbers to grant premium access.
              </p>
            </div>
  
            <form [formGroup]="premiumForm" (ngSubmit)="onSubmit()" novalidate>
  
              <!-- Dynamic phone fields -->
              <div formArrayName="phoneNumbers" class="space-y-3">
                <div
                  *ngFor="let ctrl of phoneNumbers.controls; let i = index"
                  [formGroupName]="i">
  
                  <label [for]="'phone-' + i" class="block text-xs font-semibold text-slate-600 mb-1.5">
                    Phone Number{{ phoneNumbers.length > 1 ? ' ' + (i + 1) : '' }}
                  </label>
  
                  <div class="flex items-start gap-2">
                    <div class="flex-1">
                      <div
                        class="flex items-center rounded-xl border transition-all duration-150"
                        [ngClass]="{
                          'border-red-400 bg-red-50 ring-1 ring-red-300': isPhoneInvalid(i),
                          'border-slate-300 bg-white hover:border-indigo-400 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100': !isPhoneInvalid(i)
                        }">
                        <span class="pl-3.5 pr-2 text-sm text-slate-400 select-none border-r border-slate-200 py-2.5">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                          </svg>
                        </span>
                        <input
                          [id]="'phone-' + i"
                          formControlName="phone"
                          type="tel"
                          placeholder="+91 98765 43210"
                          autocomplete="tel"
                          class="flex-1 px-3 py-2.5 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none" />
                      </div>
  
                      <!-- Inline field error -->
                      <p *ngIf="isPhoneInvalid(i)" class="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <svg class="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                        </svg>
                        {{ getPhoneError(i) }}
                      </p>
                    </div>
  
                    <!-- Remove button -->
                    <button
                      *ngIf="phoneNumbers.length > 1"
                      type="button"
                      (click)="removePhone(i)"
                      class="mt-0.5 shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 hover:bg-red-50 transition-all focus:outline-none focus:ring-2 focus:ring-red-200"
                      [attr.aria-label]="'Remove phone ' + (i + 1)">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
  
              <!-- Duplicate warning -->
              <div *ngIf="hasDuplicates()" class="mt-3 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-700">
                <svg class="w-4 h-4 shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                Duplicate phone numbers detected. Each number must be unique.
              </div>
  
              <!-- Add another -->
              <button
                *ngIf="phoneNumbers.length < 10"
                type="button"
                (click)="addPhone()"
                class="mt-4 flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors focus:outline-none group">
                <span class="w-6 h-6 rounded-full border-2 border-indigo-300 group-hover:border-indigo-600 flex items-center justify-center transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
                Add another number
                <span class="text-xs text-slate-400 font-normal">({{ phoneNumbers.length }}/10)</span>
              </button>
  
              <!-- API error -->
              <div *ngIf="submitError" class="mt-5 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <svg class="mt-0.5 w-4 h-4 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                {{ submitError }}
              </div>
  
              <div class="border-t border-slate-100 my-6"></div>
  
              <!-- Form actions -->
              <div class="flex items-center justify-between gap-3">
                <button
                  type="button"
                  (click)="resetAll()"
                  [disabled]="isSubmitting"
                  class="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50">
                  Clear
                </button>
                <button
                  type="submit"
                  [disabled]="isSubmitting || premiumForm.invalid || hasDuplicates()"
                  class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold transition-all hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  <svg *ngIf="isSubmitting" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <svg *ngIf="!isSubmitting" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ isSubmitting ? 'Granting Access…' : 'Grant Premium Access' }}
                </button>
              </div>
  
            </form>
          </div>
          <!-- End Form Card -->
  
  
          <!-- ════ RESULT CARD (shown after API response) ════════════════════════ -->
          <div *ngIf="result" class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
  
            <!-- Summary Bar -->
            <div class="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-bold text-slate-800">Access Grant Summary</h3>
                <p class="text-xs text-slate-500 mt-0.5">
                  {{ result.summary.requested }} number{{ result.summary.requested !== 1 ? 's' : '' }} submitted
                </p>
              </div>
              <!-- Stat pills -->
              <div class="flex flex-wrap gap-2">
                <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {{ result.summary.upgraded }} Upgraded
                </span>
                <span *ngIf="result.summary.alreadyPremium > 0"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  {{ result.summary.alreadyPremium }} Already Premium
                </span>
                <span *ngIf="result.summary.notFound > 0"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                  {{ result.summary.notFound }} Not Found
                </span>
              </div>
            </div>
  
            <div class="divide-y divide-slate-100">
  
              <!-- ── Section: Upgraded ────────────────────────────────────────── -->
              <div *ngIf="result.upgraded.length > 0" class="px-6 py-5">
                <div class="flex items-center gap-2 mb-4">
                  <div class="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-bold text-emerald-700">Successfully Upgraded</h4>
                </div>
  
                <div class="space-y-3">
                  <div *ngFor="let user of result.upgraded"
                    class="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                    <!-- Avatar -->
                    <div class="shrink-0 w-9 h-9 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center text-sm font-bold">
                      {{ avatarLetter(user.name) }}
                    </div>
                    <!-- Details -->
                    <div class="flex-1 min-w-0">
                      <div class="flex flex-wrap items-center gap-2 mb-1">
                        <p class="text-sm font-semibold text-slate-800">{{ user.name }}</p>
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                          </svg>
                          {{ user.membershipType }}
                        </span>
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                          {{ user.subscriptionStatus }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500 mb-2.5">{{ user.phone }}</p>
                      <div class="flex flex-wrap gap-x-5 gap-y-1">
                        <div class="text-xs text-slate-500">
                          <span class="font-semibold text-slate-600">Start: </span>{{ formatDate(user.subscriptionStartDate) }}
                        </div>
                        <div class="text-xs text-slate-500">
                          <span class="font-semibold text-slate-600">Expires: </span>{{ formatDate(user.subscriptionEndDate) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- ── Section: Already Premium ────────────────────────────────── -->
              <div *ngIf="result.alreadyPremium.length > 0" class="px-6 py-5">
                <div class="flex items-center gap-2 mb-4">
                  <div class="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <svg class="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-bold text-amber-700">Already Premium — No Changes Made</h4>
                </div>
  
                <div class="space-y-3">
                  <div *ngFor="let user of result.alreadyPremium"
                    class="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl bg-amber-50 border border-amber-100 p-4">
                    <!-- Avatar -->
                    <div class="shrink-0 w-9 h-9 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-sm font-bold">
                      {{ avatarLetter(user.name) }}
                    </div>
                    <!-- Details -->
                    <div class="flex-1 min-w-0">
                      <div class="flex flex-wrap items-center gap-2 mb-1">
                        <p class="text-sm font-semibold text-slate-800">{{ user.name }}</p>
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          Already {{ user.existingSubscription.membershipType }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500 mb-2.5">{{ user.phone }}</p>
                      <!-- Existing subscription notice -->
                      <div class="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
                        Active subscription:
                        <span class="font-semibold">{{ formatDate(user.existingSubscription.subscriptionStartDate) }}</span>
                        →
                        <span class="font-semibold">{{ formatDate(user.existingSubscription.subscriptionEndDate) }}</span>.
                        No action taken.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- ── Section: Not Found ──────────────────────────────────────── -->
              <div *ngIf="result.notFound.length > 0" class="px-6 py-5">
                <div class="flex items-center gap-2 mb-4">
                  <div class="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                    <svg class="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-bold text-red-600">Not Found in System</h4>
                </div>
  
                <div class="flex flex-wrap gap-2">
                  <div *ngFor="let phone of result.notFound"
                    class="inline-flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5">
                    <svg class="w-4 h-4 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                    <span class="text-sm font-medium text-red-700">{{ phone }}</span>
                    <span class="text-xs text-red-400">— No account found</span>
                  </div>
                </div>
              </div>
  
            </div>
  
            <!-- Result Footer -->
            <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                (click)="resetAll()"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Grant More Access
              </button>
            </div>
  
          </div>
          <!-- End Result Card -->
  
        </div>
        <!-- End Right -->
  
      </div>
    </div>
  </div>